/*----------------------------------------------------------------
  Type-detection helpers – v4 (column-aware)
----------------------------------------------------------------*/
export type DataType = "number" | "date" | "boolean" | "string";

/* ── recognise temporal columns ──────────────────────────────── */

export function isLikelyDateColumn(name = ""): boolean {
  return /(date|time|timestamp|_at|_on)$/i.test(name);
}

/** Return true when DuckDB’s declared type is DATE / TIMESTAMP … */
export function isDuckDateType(dbType = ""): boolean {
  return /(date|timestamp|time)/i.test(dbType);
}

/* ── numeric epoch helpers ───────────────────────────────────── */

const MS_PER_DAY = 86_400_000;

function inRange(ms: number) {
  return (
    ms >= Date.UTC(1900, 0, 1) &&  // 1900-01-01
    ms <= Date.UTC(2200, 11, 31)   // 2200-12-31
  );
}

/** DuckDB DATE32 or TIMESTAMP → epoch-ms | null */
export function toEpochMs(raw: number | bigint): number | null {
  const n = Number(raw);
  if (!Number.isFinite(n) || !Number.isInteger(n)) return null;

  /* DATE32 – days since 1970 (|n| ≤ 2e5) */
  if (Math.abs(n) <= 200_000) {
    const ms = n * MS_PER_DAY;
    return inRange(ms) ? ms : null;
  }

  /* TIMESTAMP – guess unit by digits */
  const digits = Math.trunc(Math.log10(Math.abs(n))) + 1;
  const ms =
    digits <= 10 ? n * 1_000        // sec
    : digits <= 13 ? n              // millis
    : digits <= 16 ? Math.trunc(n / 1_000) // micros
    : NaN;

  return inRange(ms) ? ms : null;
}

export function isParsableDateStr(s: string): boolean {
  if (s.length < 6 || s.length > 40) return false;
  if (/^\d+$/.test(s)) return false;
  return !isNaN(Date.parse(s));
}

/* ── main detector ───────────────────────────────────────────── */

export function detectDataType(
  value: unknown,
  columnName?: string,
  duckType?: string,          // optional: declared type from PRAGMA table_info
): DataType {
  if (value === null || value === undefined) return "string";
  if (value instanceof Date) return "date";
  if (typeof value === "boolean") return "boolean";

  const looksTemporal =
    isDuckDateType(duckType) || isLikelyDateColumn(columnName);

  if ((typeof value === "number" || typeof value === "bigint") && looksTemporal) {
    return toEpochMs(value) !== null ? "date" : "number";
  }

  if (typeof value === "string" && looksTemporal && isParsableDateStr(value)) {
    return "date";
  }

  return typeof value === "number" || typeof value === "bigint" ? "number" : "string";
}
